import { useState, useEffect, useCallback } from 'react';
import {
    getClassAttendees,
    getGuestRegistrations,
    getWaitlist
} from '../api/classesApi';

/**
 * Custom hook for managing class data (attendees, guests, waitlist)
 * @param {Object} cls - The class object
 * @param {boolean} open - Whether the modal is open
 * @param {string} userRole - User's role (affiliate, trainer, regular)
 * @param {boolean} selectedAffiliateListVisible - Visibility flag
 * @param {boolean} isAllClassListVisible - Visibility flag
 * @param {boolean} isTemporaryTrainer - Whether current user is temporary trainer for this class
 * @param {boolean} hasActivePlanAtAffiliate - Whether user has an active plan at this club
 * @returns {Object} - Class data and methods
 */
export function useClassData(
    cls,
    open,
    userRole,
    selectedAffiliateListVisible,
    isAllClassListVisible,
    isTemporaryTrainer = false,
    hasActivePlanAtAffiliate = false
) {
    const [attendees, setAttendees] = useState([]);
    const [guestRegistrations, setGuestRegistrations] = useState([]);
    const [waitlistEntries, setWaitlistEntries] = useState([]);
    const [waitlistCount, setWaitlistCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const canManageClass = userRole === "affiliate" || userRole === "trainer";

    // Fetch attendees
    const fetchAttendees = useCallback(async () => {
        if (!cls?.id) return;
        
        try {
            const fetchedAttendees = await getClassAttendees(cls.id);
            setAttendees(fetchedAttendees || []);
        } catch (error) {
            console.error("Error fetching attendees:", error);
            setAttendees([]);
        }
    }, [cls?.id]);

    // Fetch guest registrations
    const fetchGuestRegistrations = useCallback(async () => {
        if (!cls?.id) return;
        
        try {
            const guests = await getGuestRegistrations(cls.id);
            setGuestRegistrations(guests || []);
        } catch (error) {
            console.error("Error fetching guest registrations:", error);
            setGuestRegistrations([]);
        }
    }, [cls?.id]);

    // Fetch waitlist entries (also updates waitlist count to avoid duplicate API calls)
    const fetchWaitlistEntries = useCallback(async () => {
        if (!cls?.id) return;
        
        try {
            const waitlist = await getWaitlist(cls.id);
            setWaitlistEntries(waitlist || []);
            setWaitlistCount(waitlist ? waitlist.length : 0); // Update count at the same time
        } catch (error) {
            console.error("Error fetching waitlist:", error);
            setWaitlistEntries([]);
            setWaitlistCount(0);
        }
    }, [cls?.id]);

    // Fetch waitlist count
    const fetchWaitlistCount = useCallback(async () => {
        if (!cls?.id) return;
        
        try {
            const waitlist = await getWaitlist(cls.id);
            setWaitlistCount(waitlist ? waitlist.length : 0);
        } catch (error) {
            console.error("Error fetching waitlist count:", error);
            setWaitlistCount(0);
        }
    }, [cls?.id]);

    // Refresh all data
    const refreshAllData = useCallback(async () => {
        setIsLoading(true);
        if (isTemporaryTrainer && !canManageClass) {
            await fetchAttendees();
            setGuestRegistrations([]);
            setWaitlistEntries([]);
            setWaitlistCount(0);
            setIsLoading(false);
            return;
        }

        await Promise.all([
            fetchAttendees(),
            fetchGuestRegistrations(),
            // Only fetch waitlist once (fetchWaitlistEntries already sets waitlistCount)
            canManageClass ? fetchWaitlistEntries() : Promise.resolve()
        ]);
        setIsLoading(false);
    }, [
        fetchAttendees,
        fetchGuestRegistrations,
        fetchWaitlistEntries,
        canManageClass,
        isTemporaryTrainer
    ]);

    // Effect to load data when modal opens
    useEffect(() => {
        if (!open || !cls?.id) {
            // Reset loading state when modal closes
            if (!open) {
                setIsLoading(true);
            }
            return;
        }

        const shouldFetchData = 
            canManageClass ||
            isTemporaryTrainer ||
            selectedAffiliateListVisible === true || 
            (isAllClassListVisible === true && hasActivePlanAtAffiliate === true);

        if (shouldFetchData) {
            refreshAllData();
        } else {
            // For regular users, set loading to false immediately since data comes from cls object
            setIsLoading(false);
        }
    }, [
        open,
        cls?.id,
        canManageClass,
        isTemporaryTrainer,
        selectedAffiliateListVisible,
        isAllClassListVisible,
        hasActivePlanAtAffiliate,
        refreshAllData
    ]); // Re-run when any of these change

    return {
        attendees,
        guestRegistrations,
        waitlistEntries,
        waitlistCount,
        isLoading,
        fetchAttendees,
        fetchGuestRegistrations,
        fetchWaitlistEntries,
        fetchWaitlistCount,
        refreshAllData,
        setAttendees,
        setGuestRegistrations,
        setWaitlistEntries,
        setWaitlistCount
    };
}

